import { DateTime } from 'luxon'
import { match } from 'ts-pattern'

import {
  type AnalysisResponse,
  type FindingAggregateResponseStatistics,
  type FindingBackreferenceLinks,
  type FindingResponse,
  type FindingResultPaginatedResponse,
  type FindingResultResponse,
  type FixResultResponse,
  type ResultsScaResultResponse,
  type ResultsTriageResultResponse,
} from './api-client/api-schemas'
import { formatDuration } from './date-utils'

export const getFirstRepresentativeTriageResult = (finding: FindingResponse): ResultsTriageResultResponse | undefined =>
  finding._embedded?.['representative-results']?._embedded.items.find(result => result.type === 'triage')

export const getFirstRepresentativeFixResult = (finding: FindingResponse): FixResultResponse | undefined =>
  finding._embedded?.['representative-results']?._embedded.items.find(result => result.type === 'fix')

export const getFirstRepresentativeScaResult = (finding: FindingResponse): ResultsScaResultResponse | undefined =>
  finding._embedded?.['representative-results']?._embedded.items.find(result => result.type === 'sca')

const isCompletedTriageResult = (
  result: FindingResultResponse
): result is ResultsTriageResultResponse & {
  outcome: Extract<ResultsTriageResultResponse['outcome'], { type: 'completed-triage' }>
} => result.type === 'triage' && result.outcome?.type === 'completed-triage'

export const getFirstCompletedRepresentativeTriageResult = (finding?: FindingResponse) =>
  finding?._embedded?.['representative-results']?._embedded.items.find(isCompletedTriageResult)

const isCompletedFixResult = (
  result: FindingResultResponse
): result is FixResultResponse & { outcome: Extract<FixResultResponse['outcome'], { type: 'completed-fix' }> } =>
  result.type === 'fix' && result.outcome?.type === 'completed-fix'

export const getFirstCompletedRepresentativeFixResult = (finding?: FindingResponse) =>
  finding?._embedded?.['representative-results']?._embedded.items.find(isCompletedFixResult)

export const hasExistingPatch = (finding?: FindingResponse): boolean => {
  const fixResult = getFirstCompletedRepresentativeFixResult(finding)
  return fixResult?._embedded?.['latest-patch']?._embedded?.items?.[0]?.state !== undefined
}

const isCompletedScaResult = (
  result: FindingResultResponse
): result is ResultsScaResultResponse & {
  outcome: Extract<ResultsScaResultResponse['outcome'], { type: 'completed-sca' }>
} => result.type === 'sca' && result.outcome?.type === 'completed-sca'

export const getFirstCompletedRepresentativeScaResult = (finding?: FindingResponse) =>
  finding?._embedded?.['representative-results']?._embedded.items.find(isCompletedScaResult)

export const getFirstCompletedTriageResultFromResults = (results?: FindingResultPaginatedResponse) =>
  results?._embedded?.items.find(isCompletedTriageResult)

export const getFirstCompletedFixResultFromResults = (results?: FindingResultPaginatedResponse) =>
  results?._embedded?.items.find(isCompletedFixResult)

const isFixResult = (result: FindingResultResponse): result is Extract<FindingResultResponse, { type: 'fix' }> =>
  result.type === 'fix'

export const getFirstFixResultFromResults = (results?: FindingResultPaginatedResponse) =>
  results?._embedded?.items.find(isFixResult)

export const getFirstCompletedScaResultFromResults = (results?: FindingResultPaginatedResponse) =>
  results?._embedded?.items.find(isCompletedScaResult)

const isScaResult = (result: FindingResultResponse): result is ResultsScaResultResponse => result.type === 'sca'

export const getFirstScaResultFromResults = (results?: FindingResultPaginatedResponse) =>
  results?._embedded?.items.find(isScaResult)

export const getResultById = <T extends FindingResultResponse['type']>({
  results,
  resultId,
  type,
}: {
  results: FindingResultPaginatedResponse | undefined
  resultId: string
  type: T
}) =>
  results?._embedded?.items
    .filter((r): r is Extract<FindingResultResponse, { type: T }> => r.type === type)
    .find(r => r.id === resultId)

export const hasInProgressPatch = (finding: FindingResponse): boolean => {
  const fixResult = getFirstCompletedRepresentativeFixResult(finding)
  return fixResult?._embedded?.['latest-patch']?._embedded?.items?.[0]?.state === 'in-progress'
}

export const calculateFindingsWithResultsHasAnyInProgressAnalysis = (
  statistics: FindingAggregateResponseStatistics
): boolean => {
  const triageInProgress = statistics.triage.status.in_progress
  const fixInProgress = statistics.fix.status.in_progress
  const scaInProgress = statistics.sca.status.in_progress

  return triageInProgress > 0 || fixInProgress > 0 || scaInProgress > 0
}

export const calculateFindingsWithResultsHasAnyAnalysis = (statistics: FindingAggregateResponseStatistics): boolean => {
  const triageTotal =
    statistics.triage.status.completed +
    statistics.triage.status.failed +
    statistics.triage.status.no_recommendations +
    statistics.triage.status.in_progress

  const fixTotal =
    statistics.fix.status.completed +
    statistics.fix.status.failed +
    statistics.fix.status.no_recommendations +
    statistics.fix.status.in_progress

  const scaTotal = statistics.sca.status.completed + statistics.sca.status.failed + statistics.sca.status.in_progress

  return triageTotal > 0 || fixTotal > 0 || scaTotal > 0
}

/**
 * Determines the analysis status for a finding's outer row indicator.
 *
 * Returns:
 * - 'first-analysis-in-progress': Representative result has no outcome and no previous completed results exist
 * - 'reanalysis-in-progress': Representative result has no outcome but previous completed results exist
 * - 'representative-not-latest': Representative result is complete but not the most recent analysis
 * - null: No indicator needed (representative is complete and latest, or no results)
 */
export type AnalysisIndicatorStatus =
  'first-analysis-in-progress' | 'reanalysis-in-progress' | 'representative-not-latest' | null

/**
 * Returns true if the finding's analysis is currently in progress (first analysis or reanalysis).
 */
export const isAnalysisInProgress = (finding: FindingResponse): boolean => {
  const status = getAnalysisIndicatorStatus(finding)
  return status === 'first-analysis-in-progress' || status === 'reanalysis-in-progress'
}

const NEWLY_ANALYZED_WINDOW_MINUTES = 10

export const isRecentlyAnalyzed = (finding: FindingResponse): boolean => {
  const representativeResults = finding._embedded?.['representative-results']?._embedded.items
  if (!representativeResults?.length) return false

  const timestamps = representativeResults
    .map(r => r.completed)
    .filter((t): t is string => t !== null && t !== undefined)

  if (!timestamps.length) return false

  const latestTimestamp = timestamps.reduce((latest, t) => (t > latest ? t : latest))
  const completedTime = DateTime.fromISO(latestTimestamp)
  if (!completedTime.isValid) return false

  return completedTime > DateTime.now().minus({ minutes: NEWLY_ANALYZED_WINDOW_MINUTES })
}

export const getAnalysisIndicatorStatus = (finding: FindingResponse): AnalysisIndicatorStatus => {
  const representativeResults = finding._embedded?.['representative-results']?._embedded?.items
  const allResults = finding._embedded?.results?._embedded?.items

  if (!representativeResults?.length) {
    return null
  }

  const representativeTriageOrSca = representativeResults.find(r => r.type === 'triage' || r.type === 'sca')
  if (!representativeTriageOrSca) {
    return null
  }

  const representativeHasNoOutcome = !representativeTriageOrSca.outcome

  if (representativeHasNoOutcome) {
    if (!allResults?.length) {
      return 'first-analysis-in-progress'
    }

    const representativeAnalysisHref = representativeTriageOrSca._links.analysis.href

    const hasOtherCompletedResults = allResults.some(result => {
      if (result._links.analysis.href === representativeAnalysisHref) {
        return false
      }
      return Boolean(result.outcome)
    })

    return hasOtherCompletedResults ? 'reanalysis-in-progress' : 'first-analysis-in-progress'
  }

  if (!allResults?.length) {
    return null
  }

  const representativeAnalysisHref = representativeTriageOrSca._links.analysis.href
  const representativeTimestamp = representativeTriageOrSca.completed

  if (!representativeTimestamp) {
    return null
  }

  // Check if there's an in-progress result from a different analysis (reanalysis scenario)
  // This happens when representative is completed but a new analysis is running
  // Only consider triage/sca results since fix results are a later pipeline stage
  const hasInProgressResultFromOtherAnalysis = allResults.some(result => {
    if (result.type === 'fix') return false
    if (result._links.analysis.href === representativeAnalysisHref) {
      return false
    }
    return !result.outcome
  })

  if (hasInProgressResultFromOtherAnalysis) {
    return 'reanalysis-in-progress'
  }

  // Only compare triage/sca results since fix results are a later pipeline stage
  // and their completion time doesn't indicate a newer analysis
  const hasNewerCompletedResult = allResults.some(result => {
    if (result.type === 'fix') return false
    if (result._links.analysis.href === representativeAnalysisHref) {
      return false
    }
    if (!result.outcome) {
      return false
    }
    if (!result.completed) {
      return false
    }
    return DateTime.fromISO(result.completed) > DateTime.fromISO(representativeTimestamp)
  })

  return hasNewerCompletedResult ? 'representative-not-latest' : null
}

export function reformatFindingTriageArticleMarkdown(markdown: undefined): undefined
export function reformatFindingTriageArticleMarkdown(markdown: string): string
export function reformatFindingTriageArticleMarkdown(markdown: string | undefined): string | undefined
export function reformatFindingTriageArticleMarkdown(markdown: string | undefined) {
  return markdown
    ?.replace(/<details>/g, '')
    .replace(/<\/details>/g, '')
    .replace(/\s*<i>Suggestion:.*?<\/i><br\/>\s*/g, '')
    .split('\n')
    .map(line => line.trim())
    .join('\n')
    .trim()
}

export const getAnalysisMetadata = ({
  analysis,
  resultCompleted,
}: {
  analysis?: AnalysisResponse
  resultCompleted?: string | null
}) => {
  if (!analysis || !resultCompleted) {
    return undefined
  }

  const startLog = analysis.log.find(l => l.state === 'queued')
  if (!startLog) {
    return undefined
  }

  return {
    startDate: startLog.timestamp,
    duration: formatDuration(startLog.timestamp, resultCompleted),
  }
}

export const reformatFindingTriageReportMarkdown = (markdown?: string) =>
  markdown?.replace(/# Risk Recommendations\n\n\n\s+/g, '# Risk Recommendations\n\n')

export const inferLanguageFromFilename = (filename: string) => {
  const extension = filename.split('.').pop()
  return match(extension)
    .with('js', () => 'javascript')
    .with('py', () => 'python')
    .with('rb', () => 'ruby')
    .with('ts', () => 'typescript')
    .with('tsx', () => 'typescript')
    .with('jsx', () => 'javascript')
    .with('java', () => 'java')
    .with('c', () => 'c')
    .with('cpp', () => 'cpp')
    .with('cs', () => 'csharp')
    .with('php', () => 'php')
    .with('go', () => 'go')
    .with('rs', () => 'rust')
    .with('swift', () => 'swift')
    .with('kt', () => 'kotlin')
    .with('scala', () => 'scala')
    .with('pl', () => 'perl')
    .with('sh', () => 'bash')
    .with('bash', () => 'bash')
    .with('zsh', () => 'bash')
    .with('fish', () => 'bash')
    .with('sql', () => 'sql')
    .with('html', () => 'html')
    .with('css', () => 'css')
    .with('scss', () => 'scss')
    .with('sass', () => 'sass')
    .with('less', () => 'less')
    .with('json', () => 'json')
    .with('xml', () => 'xml')
    .with('yaml', () => 'yaml')
    .with('yml', () => 'yaml')
    .with('toml', () => 'toml')
    .with('ini', () => 'ini')
    .with('md', () => 'markdown')
    .with('markdown', () => 'markdown')
    .with('txt', () => 'text')
    .with('log', () => 'text')
    .with('csv', () => 'csv')
    .with('tsv', () => 'csv')
    .otherwise(() => 'text')
}

/**
 * Wraps content in a markdown code fence that is guaranteed not to be broken
 * by fences inside the content: per CommonMark, a fence of N backticks can
 * safely contain runs of up to N-1 backticks, so use one backtick more than
 * the longest run found in the content (minimum the standard three).
 */
export const wrapInCodeFence = (content: string, language: string) => {
  const longestBacktickRun = content.match(/`+/g)?.reduce((max, run) => Math.max(max, run.length), 0) ?? 0
  const fence = '`'.repeat(Math.max(3, longestBacktickRun + 1))
  return `${fence}${language}\n${content}\n${fence}`
}

/**
 * The scan a finding backreference points at.
 *
 * A backreference's `_links` are REST API paths
 * (`/api/v1/scans/{scanId}/findings/{findingId}`), not UI routes, and the scan id
 * appears nowhere else on the embedded shape — so anything that wants to route to
 * the finding has to read it back out of the href. `self` is the canonical
 * relation (`finding` advertises the same URL under the semantic name), and the
 * pattern is anchored so a differently-shaped path fails loudly rather than
 * matching some substring of it.
 *
 * `_links` is absent as a whole when the backreference did not resolve, which is
 * the only expected miss.
 *
 * @param links the backreference's `_links`, if it has any
 * @returns the scan id, or `undefined` when there is no resolvable backreference
 */
export const scanIdFromFindingBackreference = (links: FindingBackreferenceLinks | undefined): string | undefined =>
  /^\/api\/v1\/scans\/([^/]+)\/findings\//.exec(links?.self.href ?? '')?.[1]
